import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { usePageSeo } from "@/lib/seo";

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const detail = [
  {
    id: "crisis-management",
    no: "01",
    title: "Crisis Management",
    lede: "Crisis management help when a story is already moving.",
    body: [
      "When litigation, an investigation, or a fast-moving story puts your reputation at risk, you need a crisis PR firm that can take control of the narrative in the first hour, not the first week. We verify the facts, define the message, and coordinate every channel — media, employees, investors, regulators and the public — so everyone hears one clear, defensible version of events.",
      "We work quietly alongside your leadership team and your lawyers, protecting privilege and trust at every step. Whether you need to hire a crisis communications consultant for a single acute event or an ongoing threat, we bring a steady hand and a written plan you can actually execute.",
    ],
    points: ["Rapid-response strategy & statements", "Media triage and reporter management", "Litigation and investigation communications", "Employee, investor & stakeholder messaging"],
  },
  {
    id: "strategic-communications",
    no: "02",
    title: "Strategic Communications",
    lede: "A strategic communications agency for the long game.",
    body: [
      "Reputation is built long before it is tested. We develop the message architecture, positioning and narrative strategy that align every stakeholder around a single, credible story — then we make sure that story shows up consistently across every place your audiences look.",
      "From executive positioning to brand repositioning and issue campaigns, we help you decide which rooms you need to win and give you the language to win them.",
    ],
    points: ["Message architecture & positioning", "Executive and founder visibility", "Narrative and campaign strategy", "Announcement and launch planning"],
  },
  {
    id: "media-relations",
    no: "03",
    title: "Media Relations",
    lede: "Real relationships with the reporters who shape perception.",
    body: [
      "Coverage that lasts comes from trust, not blast emails. We maintain genuine relationships with national and trade journalists, and we know how to place, shape and steward the stories that define how you are seen.",
      "We pitch selectively, prepare you thoroughly, and protect you in the room — so the coverage that runs is accurate, fair and durable.",
    ],
    points: ["Targeted press outreach & placement", "Media training and prep", "Interview and briefing support", "Trade, national and broadcast coverage"],
  },
  {
    id: "reputation-advisory",
    no: "04",
    title: "Reputation Advisory",
    lede: "Long-horizon guidance for credibility that compounds.",
    body: [
      "For executives and institutions, reputation is an asset to be managed deliberately. We provide ongoing reputation advisory — monitoring perception, anticipating risk, and guiding the decisions and communications that protect credibility across every audience that matters.",
      "It is the quiet, continuous work that keeps a crisis from happening in the first place.",
    ],
    points: ["Reputation audits & risk mapping", "Ongoing advisory & counsel", "Issue and stakeholder monitoring", "Board and leadership communications"],
  },
];

const steps = [
  { n: "1", t: "Confidential intake", d: "Tell us where things stand. We listen first and read every message personally." },
  { n: "2", t: "Strategy & plan", d: "We map the landscape — stakeholders, risks and the narrative — and give you a clear, written plan." },
  { n: "3", t: "Execution", d: "We manage the media, the message and the moving parts, with you in control the whole way." },
  { n: "4", t: "Steady state", d: "We help you hold the gains and build the reputation that lasts beyond the headline." },
];

const CTA = ({ testid }) => (
  <a
    href="/#contact"
    data-testid={testid}
    className="group inline-flex items-center gap-3 bg-gold hover:bg-ivory text-navy px-8 py-4 overline transition-colors"
  >
    Start a conversation <span className="transition-transform group-hover:translate-x-1">&rarr;</span>
  </a>
);

export default function Services() {
  usePageSeo({
    path: "/services",
    title: "Services | Crisis PR & Strategic Communications | Park Communications",
    description:
      "Crisis management help, crisis PR, media relations and reputation advisory. Park Communications is a strategic communications agency — hire a crisis communications consultant for high-stakes moments.",
  });

  return (
    <main data-testid="services-page">
      <Header solid linkBase="/" />

      {/* Hero */}
      <section className="relative bg-navy text-ivory grain overflow-hidden pt-40 pb-20">
        <div className="relative mx-auto max-w-7xl px-6">
          <Link to="/" data-testid="services-back" className="overline text-ivory/40 hover:text-gold transition-colors">
            &larr; Back to home
          </Link>
          <p className="overline text-gold mt-10 mb-6">Services</p>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.03] max-w-4xl">
            Crisis PR and strategic communications, when it counts.
          </h1>
          <p className="text-ivory/70 text-lg leading-relaxed max-w-2xl mt-8">
            Park Communications is a strategic communications agency and crisis PR firm. From crisis management help in
            the first hour to long-term reputation advisory, we give executives, legal teams, brands and institutions a
            clear plan and a steady hand. If you need to hire a crisis communications consultant, start here.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
            <CTA testid="services-hero-cta" />
            <a href="mailto:info@parkcomms.com" data-testid="services-hero-email" className="overline text-ivory/70 hover:text-gold transition-colors">
              or email info@parkcomms.com
            </a>
          </div>
        </div>
      </section>

      {/* Who we help */}
      <section className="bg-ivory py-16 border-b border-navy/10">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <p className="overline text-gold-dark mb-4">Who we help</p>
          <p className="font-serif font-light text-2xl sm:text-3xl leading-snug text-navy">
            A public relations agency for executives and CEOs, legal teams, entertainment, athletes, actors and
            musicians, founders and public figures, and government, nonprofit and enterprise institutions.
          </p>
          <p className="text-navy/55 text-sm mt-6 max-w-2xl mx-auto">
            A crisis communications firm serving clients in Los Angeles, across California, and in Washington, DC —
            available for emergency crisis communications support nationwide.
          </p>
        </div>
      </section>

      {/* Service detail blocks */}
      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-6">
          {detail.map((s) => (
            <motion.div
              key={s.id}
              id={s.id}
              data-testid={`service-detail-${s.id}`}
              variants={fade}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="grid lg:grid-cols-12 gap-8 lg:gap-16 py-20 border-t border-navy/15 scroll-mt-28"
            >
              <div className="lg:col-span-4">
                <span className="font-serif text-2xl text-gold-dark">{s.no}</span>
                <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-4">{s.title}</h2>
                <p className="text-navy/60 mt-4 text-lg">{s.lede}</p>
              </div>
              <div className="lg:col-span-5 space-y-5 text-navy/75 leading-relaxed">
                {s.body.map((p, i) => <p key={i}>{p}</p>)}
                <a href="/#contact" data-testid={`service-cta-${s.id}`} className="inline-block overline text-navy hover:text-gold-dark transition-colors pt-2 link-underline">
                  Talk to us about this &rarr;
                </a>
              </div>
              <div className="lg:col-span-3">
                <p className="overline text-gold-dark mb-4">What this looks like</p>
                <ul className="space-y-3">
                  {s.points.map((pt) => (
                    <li key={pt} className="text-navy/70 border-b border-navy/10 pb-3 flex gap-3">
                      <span className="text-gold-dark">—</span>{pt}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="bg-navy text-ivory grain relative overflow-hidden py-24">
        <div className="relative mx-auto max-w-7xl px-6">
          <p className="overline text-gold mb-6">How we work</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-[1.05] max-w-2xl mb-16">
            A clear path from first call to steady state.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/12">
            {steps.map((st) => (
              <div key={st.n} className="border-b border-r border-white/12 p-8">
                <span className="font-serif text-3xl text-gold">{st.n}</span>
                <h3 className="font-serif text-2xl mt-4 mb-2">{st.t}</h3>
                <p className="text-ivory/60 leading-relaxed">{st.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-ivory py-24">
        <div className="mx-auto max-w-3xl px-6">
          <p className="overline text-gold-dark mb-6">Common questions</p>
          <div className="space-y-8">
            <div className="border-t border-navy/15 pt-6">
              <h3 className="font-serif text-2xl mb-2">What does a crisis PR firm do?</h3>
              <p className="text-navy/70 leading-relaxed">A crisis PR firm gives crisis management help to organizations and individuals facing reputational threats, litigation or media scrutiny — verifying facts, shaping the framing, and coordinating messaging across every audience to protect and restore trust.</p>
            </div>
            <div className="border-t border-navy/15 pt-6">
              <h3 className="font-serif text-2xl mb-2">How do I hire a crisis communications consultant?</h3>
              <p className="text-navy/70 leading-relaxed">Reach out through our contact form or email info@parkcomms.com with a short note about your situation. Every message is read personally and held in confidence, and we will tell you honestly what your situation actually needs.</p>
            </div>
            <div className="border-t border-navy/15 pt-6">
              <h3 className="font-serif text-2xl mb-2">Do you only work during a crisis?</h3>
              <p className="text-navy/70 leading-relaxed">No. Alongside crisis work, we act as a strategic communications agency for the long game — reputation management, media relations and positioning that build credibility well before it is ever tested.</p>
            </div>
            <div className="border-t border-navy/15 pt-6">
              <h3 className="font-serif text-2xl mb-2">How much does crisis PR cost?</h3>
              <p className="text-navy/70 leading-relaxed">It depends on scope — an acute, single-event engagement is priced differently from ongoing retained counsel. After a short, confidential intake we give you a clear proposal with no obligation. Tell us what you are facing and we will be straight with you about what it takes.</p>
            </div>
            <div className="border-t border-navy/15 pt-6">
              <h3 className="font-serif text-2xl mb-2">Crisis management vs. public relations — what's the difference?</h3>
              <p className="text-navy/70 leading-relaxed">Public relations builds visibility and reputation over time; crisis management protects it under pressure. As both a public relations agency and a crisis communications firm, we do both — issues management, damage control, and the media and stakeholder communications that get you through a high-stakes moment.</p>
            </div>
            <div className="border-t border-navy/15 pt-6">
              <h3 className="font-serif text-2xl mb-2">Do you offer executive media training?</h3>
              <p className="text-navy/70 leading-relaxed">Yes. We provide executive media training and spokesperson training so leaders can handle tough interviews, hostile questions and negative press with confidence — a core part of any crisis communications plan.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-navy text-ivory grain relative overflow-hidden py-20">
        <div className="relative mx-auto max-w-7xl px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-8">
          <h2 className="font-serif font-light text-3xl sm:text-4xl max-w-xl">Facing a high-stakes moment? Let&apos;s talk, in confidence.</h2>
          <CTA testid="services-final-cta" />
        </div>
      </section>

      <Footer />
    </main>
  );
}
